import {inject, Injectable} from '@angular/core';
import {BaseApiService} from './base-api.service';
import {map, Observable} from 'rxjs';
import {IItem} from '../../types/IItem';
import {ISearchParams} from '../../types/ICart';
import {AppStore} from '../store/app.store';
import {IAddonForItem} from '../../types/IAddonForItem';
import {ISearchProgress} from '../../types/ISearchProgress';

export interface AddonRequest {
  item_id: number | string;
  brand: string;
  model: string;
  item_number: string;
  price: number;
  quantity: number;
  price_level_id: string;
  excluded_addon_ids?: (number | string)[];
}

export interface ItemPricingRequest {
  price_level_id: string | number | undefined,
  repair_order_id: string | number | undefined,
  customer_id: string | number | undefined,
  items: PricingItem[]
}

export interface PricingItem {
  item_id: string | number;
  brand: string;
  model: string;
  item_number: string;
  costs: number[];
  map: number | null;
}

export type PricingResponse = {
  brand: string;
  costs: number[];
  item_id: string | number;
  item_number: string | number;
  model: string;
  prices: Record<number | string, {price: string, price_special: string }>;
};

@Injectable({
  providedIn: 'root'
})
export class ItemsService extends BaseApiService {
  private appStore = inject(AppStore);

  search(params: ISearchParams): Observable<IItem[]> {
    return this.post<{ data: any[] }>(`clients/${this.appStore.client_id()}/carts/${this.appStore.cart().id}/search`, params).pipe(
      map(response => response.data.map(item => ({
        ...item,
        id: item.item_id,
        sourcing: [],
        availability: [
          ...item.availability,
          {
            type: 'external',
            name: 'External',
            delivery: undefined,
            quantity: 999,
            committed: 0,
            cost: item.cost || item.availability?.[0]?.cost || 0,
            cost_special: 0
          }
        ],
      })))
    );
  }

  getSearchProgress(): Observable<ISearchProgress> {
    return this.get<{ data: ISearchProgress | [] }>(`clients/${this.appStore.client_id()}/carts/${this.appStore.cart().id}/search/progress`).pipe(
      map(response => Array.isArray(response.data) ? {} : response.data)
    );
  }

  getPricing(request: ItemPricingRequest): Observable<PricingResponse[]> {
    return this.post<{data: {items: PricingResponse[]}}>(`clients/${this.appStore.client_id()}/carts/${this.appStore.cart().id}/prices`, { ...request }).pipe(
      map(response => response.data.items)
    );
  }

  getAddonsForItem(request: AddonRequest): Observable<IAddonForItem> {
    const {item_id, brand, model, item_number, price, quantity, price_level_id, excluded_addon_ids} = request;
    return this.get<{data: {addons: IAddonForItem}}>(
      `clients/${this.appStore.client_id()}/carts/${this.appStore.cart().id}/${item_id}/addons`,
      {brand, model, item_number, price, quantity, price_level_id, ...(excluded_addon_ids?.length ? {excluded_addon_ids} : {})},
    ).pipe(
      map(response => ({...response.data.addons, excluded_addon_ids}))
    );
  }
}
